import { useState } from 'react'
import { Button } from '../common/Button'
import { Card } from '../common/Card'
import type { Challenge, ChallengeAnswer } from '../../types'

interface ObservationChallengeProps {
  challenge: Challenge
  onSubmit: (answer: ChallengeAnswer) => void
  isSubmitting: boolean
  debugMode?: boolean
}

export function ObservationChallenge({
  challenge,
  onSubmit,
  isSubmitting,
  debugMode,
}: ObservationChallengeProps) {
  const [selected, setSelected] = useState<string | null>(null)

  const handleSubmit = () => {
    if (selected) {
      onSubmit({ answer: selected })
    }
  }

  return (
    <div className="space-y-4">
      <Card variant="parchment" padding="md">
        <p className="text-brown-800 italic">
          "{challenge.question || challenge.instructions}"
        </p>
      </Card>

      <div className="space-y-2">
        {challenge.options?.map((option, index) => (
          <button
            key={index}
            onClick={() => setSelected(option)}
            disabled={isSubmitting}
            className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
              selected === option
                ? 'border-primary-500 bg-primary-50'
                : 'border-cream-200 hover:border-primary-300'
            } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span className="flex items-center gap-3">
              <span
                className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                  selected === option
                    ? 'border-primary-500 bg-primary-500'
                    : 'border-cream-300'
                }`}
              >
                {selected === option && (
                  <svg
                    className="w-4 h-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </span>
              <span className="text-brown-800">{option}</span>
            </span>
          </button>
        ))}
      </div>

      {debugMode && challenge.debug_answer && (
        <p className="text-xs text-brown-400">
          Debug answer: {challenge.debug_answer}
        </p>
      )}

      <Button
        onClick={handleSubmit}
        disabled={!selected || isSubmitting}
        loading={isSubmitting}
        className="w-full"
      >
        Submit Answer
      </Button>
    </div>
  )
}
